import { createContext, type ReactNode, useContext } from "react";
import ActivitiesApi from "../activities/ActivitiesApi";
import BusinessCustomersApi from "../business/admin/customers/BusinessCustomersApi";
import BusinessTemplatesApi from "../business/admin/templates/BusinessTemplatesApi";
import BusinessCustomerMailsApi from "../business/admindev/mails/BusinessCustomerMailsApi";
import BusinessEmailDataApi from "../business/emails/BusinessEmailDataApi";
import BusinessFeedItemsApi from "../business/feed/BusinessFeedItemsApi";
import BusinessFeedCommentsApi from "../business/feedcomment/BusinessFeedCommentsApi";
import BusinessFeedCommentsReplyApi from "../business/feedcommentreply/BusinessFeedCommentsReplyApi";
import BusinessModulesApi from "../business/modules/BusinessModulesApi";
import BusinessPlansApi from "../business/plan/BusinessPlansApi";
import BusinessScheduleItemApi from "../business/schedule/BusinessScheduleItemApi";
import FeedbackApi from "../feedback/FeedbackApi";
import ModulesApi from "../modules/core/ModulesApi";
import ModulesDataApi from "../modules/core/ModulesDataApi";
import PlansApi from "../plans/PlansApi";
import SectionsApi from "../sections/SectionsApi";
import PlansTemplateApi from "../templates/PlansTemplateApi";
import SuperUsersApi from "../users/SuperUsersApi";
import UsersApi from "../users/UsersApi";
import { db } from "./firebaseInit";
import EmailDataApi from "./notifications/EmailDataApi";
import EmailRemindersApi from "./notifications/EmailRemindersApi";
import PlaniraLogApi from "./PlaniraLogApi";
import { type Stores, useStores } from "./stores";

export class Api {
  client = db;

  usersApi: UsersApi;
  superUsersApi: SuperUsersApi;
  plansApi: PlansApi;
  sectionsApi: SectionsApi;
  modulesApi: ModulesApi;
  modulesDataApi: ModulesDataApi;
  activitiesApi: ActivitiesApi;
  templatesApi: PlansTemplateApi;
  feedbackApi: FeedbackApi;

  businessCustomersApi: BusinessCustomersApi;
  businessCustomerMailsApi: BusinessCustomerMailsApi;
  businessTemplatesApi: BusinessTemplatesApi;

  businessPlansApi: BusinessPlansApi;
  businessModulesApi: BusinessModulesApi;
  businessFeedItemsApi: BusinessFeedItemsApi;
  businessFeedCommentsApi: BusinessFeedCommentsApi;
  businessFeedCommentsReplyApi: BusinessFeedCommentsReplyApi;
  businessScheduleItemApi: BusinessScheduleItemApi;
  businessEmailDataApi: BusinessEmailDataApi;

  emailDataApi: EmailDataApi;
  emailRemindersApi: EmailRemindersApi;
  planiraLogApi: PlaniraLogApi;

  constructor(stores: Stores) {
    this.usersApi = new UsersApi(this, stores);
    this.superUsersApi = new SuperUsersApi(this, stores);
    this.plansApi = new PlansApi(this, stores);
    this.sectionsApi = new SectionsApi(this, stores);
    this.modulesApi = new ModulesApi(this, stores);
    this.modulesDataApi = new ModulesDataApi(this, stores);
    this.activitiesApi = new ActivitiesApi(this, stores);
    this.templatesApi = new PlansTemplateApi(this, stores);
    this.feedbackApi = new FeedbackApi(this, stores);

    this.businessCustomersApi = new BusinessCustomersApi(this, stores);
    this.businessCustomerMailsApi = new BusinessCustomerMailsApi(this, stores);
    this.businessTemplatesApi = new BusinessTemplatesApi(this, stores);

    this.businessPlansApi = new BusinessPlansApi(this, stores);
    this.businessModulesApi = new BusinessModulesApi(this, stores);
    this.businessFeedItemsApi = new BusinessFeedItemsApi(this, stores);
    this.businessFeedCommentsApi = new BusinessFeedCommentsApi(this, stores);
    this.businessFeedCommentsReplyApi = new BusinessFeedCommentsReplyApi(
      this,
      stores,
    );
    this.businessScheduleItemApi = new BusinessScheduleItemApi(this, stores);
    this.businessEmailDataApi = new BusinessEmailDataApi(this, stores);

    this.emailDataApi = new EmailDataApi(this, stores);
    this.emailRemindersApi = new EmailRemindersApi(this, stores);
    this.planiraLogApi = new PlaniraLogApi(this, stores);
  }
}

let _api: Api;

const ApiContext = createContext<Api | undefined>(undefined);

export function ApiProvider({
  children,
  api,
}: {
  children: ReactNode;
  api?: Api;
}) {
  const stores = useStores();
  const root = api ?? api ?? new Api(stores);

  return <ApiContext.Provider value={root}>{children}</ApiContext.Provider>;
}

// create the hook
export function useApi() {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error("useApi must be used within ApiContext");
  }

  return context;
}
